import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../../services/api';
import { PageTooltip } from '../components/TooltipSystem';
import {
  MessageSquare, Phone, Shield, ChevronDown, ChevronUp, Save, Loader2,
  CheckCircle2, XCircle, AlertTriangle, Eye, EyeOff, Zap, Send, Settings2, Radio
} from 'lucide-react';

const SECTION_ICONS = {
  account: Shield,
  senders: Radio,
  broadcast: Send,
  automation: Settings2,
  transactional: Zap,
};

function StatusBadge({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
      ok ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-[#64748B]'
    }`}>
      {ok ? <CheckCircle2 size={10} /> : <XCircle size={10} />}
      {label}
    </span>
  );
}

function MaskedInput({ value, onChange, placeholder, testId }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full px-3 py-2.5 pr-10 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/20 focus:border-[#D44E20] font-mono"
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#64748B] hover:text-[#333]"
      >
        {show ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
  );
}

function FieldRow({ label, desc, children }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-2 items-start">
      <div>
        <p className="text-sm font-medium text-[#1A1A1A]">{label}</p>
        {desc && <p className="text-[10px] text-[#999] mt-0.5">{desc}</p>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function SectionCard({ id, title, icon: Icon, description, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid={`section-${id}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-slate-50/50 transition-colors"
        data-testid={`toggle-${id}`}
      >
        <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#D44E20]/8 flex-shrink-0">
          <Icon size={16} className="text-[#D44E20]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#1A1A1A]">{title}</p>
          <p className="text-[10px] text-[#64748B]">{description}</p>
        </div>
        {open ? <ChevronUp size={16} className="text-[#999]" /> : <ChevronDown size={16} className="text-[#999]" />}
      </button>
      {open && <div className="px-5 pb-5 space-y-4 border-t border-gray-50 pt-4">{children}</div>}
    </div>
  );
}

export default function AdminIntegrations() {
  const [config, setConfig] = useState({
    account_sid: '',
    auth_token: '',
    whatsapp_from: '',
    sms_from: '',
    messaging_service_sid: '',
    template_sids: {},
  });
  const [status, setStatus] = useState(null);
  const [templateLabels, setTemplateLabels] = useState({});
  const [templateCategories, setTemplateCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    adminApi.get('/admin/integrations')
      .then(res => {
        const d = res.data;
        const cfg = d.config || {};
        setConfig({
          account_sid: cfg.account_sid || '',
          auth_token: cfg.auth_token || '',
          whatsapp_from: cfg.whatsapp_from || '',
          sms_from: cfg.sms_from || '',
          messaging_service_sid: cfg.messaging_service_sid || '',
          template_sids: cfg.template_sids || {},
        });
        setStatus(d.status);
        setTemplateLabels(d.template_labels || {});
        setTemplateCategories(d.template_categories || {});
      })
      .catch(() => toast.error('Errore caricamento configurazione'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await adminApi.put('/admin/integrations', config);
      setStatus(res.data.status);
      setTestResult(null);
      toast.success('Configurazione salvata');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await adminApi.post('/admin/integrations/test');
      setTestResult({ ok: true, name: res.data.account_name, status: res.data.account_status });
      toast.success('Connessione riuscita!');
    } catch (e) {
      setTestResult({ ok: false, error: e.response?.data?.detail || 'Connessione fallita' });
      toast.error('Test fallito');
    } finally {
      setTesting(false);
    }
  };

  const updateField = (field, value) => setConfig(c => ({ ...c, [field]: value }));
  const updateTemplate = (key, value) =>
    setConfig(c => ({ ...c, template_sids: { ...c.template_sids, [key]: value } }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const categoryLabels = {
    broadcast: { title: 'Template Broadcast', desc: 'I 6 template selezionabili nella pagina Broadcast', icon: Send },
    automation: { title: 'Template Automazioni', desc: 'Template usati dalle automazioni programmate', icon: Settings2 },
    transactional: { title: 'Template Transazionali', desc: 'Template inviati automaticamente dopo azioni utente', icon: Zap },
  };

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-3xl" data-testid="admin-integrations-page">
      <PageTooltip page="integrations" />
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl tracking-tight font-bold text-[#1A1A1A]">Integrazioni</h1>
        <p className="text-[#64748B] text-sm">Configura le credenziali Twilio per WhatsApp e SMS</p>
      </div>

      {/* Status overview */}
      {status && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="integration-status">
          <StatusBadge ok={status.twilio_connected} label="Twilio" />
          <StatusBadge ok={status.whatsapp_configured} label="WhatsApp" />
          <StatusBadge ok={status.sms_configured} label="SMS" />
          <StatusBadge
            ok={status.templates_configured > 0}
            label={`Template ${status.templates_configured}/${status.templates_total}`}
          />
          <span className="text-[10px] text-[#aaa] self-center ml-1">
            Fonte: {status.source === 'database' ? 'Database' : 'Variabili ambiente'}
          </span>
        </div>
      )}

      <div className="space-y-3">
        {/* Section 1: Account */}
        <SectionCard
          id="account"
          title="Account Twilio"
          icon={Shield}
          description="Le credenziali principali del tuo account Twilio"
          defaultOpen={true}
        >
          <FieldRow label="Account SID" desc="Inizia con AC...">
            <input
              type="text"
              value={config.account_sid}
              onChange={e => updateField('account_sid', e.target.value)}
              placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              data-testid="input-account-sid"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/20 focus:border-[#D44E20] font-mono"
            />
          </FieldRow>

          <FieldRow label="Auth Token" desc="Visibile nella console Twilio">
            <MaskedInput
              value={config.auth_token}
              onChange={v => updateField('auth_token', v)}
              placeholder="Il tuo auth token Twilio"
              testId="input-auth-token"
            />
          </FieldRow>

          {/* Test button */}
          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={handleTest}
              disabled={testing || !config.account_sid || !config.auth_token}
              data-testid="test-connection-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-[#111114] text-white hover:bg-[#333] disabled:opacity-40 transition-all"
            >
              {testing ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
              Testa connessione
            </button>
            {testResult && (
              <div className={`flex items-center gap-1.5 text-xs font-medium ${testResult.ok ? 'text-green-600' : 'text-red-500'}`} data-testid="test-result">
                {testResult.ok ? (
                  <><CheckCircle2 size={14} /> {testResult.name} ({testResult.status})</>
                ) : (
                  <><XCircle size={14} /> {testResult.error}</>
                )}
              </div>
            )}
          </div>
        </SectionCard>

        {/* Section 2: Senders */}
        <SectionCard
          id="senders"
          title="Numeri Sender"
          icon={Radio}
          description="I numeri da cui vengono inviati WhatsApp e SMS"
          defaultOpen={true}
        >
          <FieldRow label="WhatsApp Business" desc="Formato: whatsapp:+39xxxxxxxxxx">
            <input
              type="text"
              value={config.whatsapp_from}
              onChange={e => updateField('whatsapp_from', e.target.value)}
              placeholder="whatsapp:+393933655860"
              data-testid="input-whatsapp-from"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/20 focus:border-[#D44E20] font-mono"
            />
          </FieldRow>

          <FieldRow label="SMS Sender" desc="Numero o Sender ID alfanumerico">
            <input
              type="text"
              value={config.sms_from}
              onChange={e => updateField('sms_from', e.target.value)}
              placeholder="+393933655860 oppure NomeLocale"
              data-testid="input-sms-from"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/20 focus:border-[#D44E20] font-mono"
            />
          </FieldRow>

          <FieldRow label="Messaging Service SID" desc="Opzionale, per SMS con pool di numeri">
            <input
              type="text"
              value={config.messaging_service_sid}
              onChange={e => updateField('messaging_service_sid', e.target.value)}
              placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              data-testid="input-messaging-service-sid"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/20 focus:border-[#D44E20] font-mono"
            />
          </FieldRow>
        </SectionCard>

        {/* Template SID sections */}
        {Object.entries(templateCategories).map(([catKey, templateKeys]) => {
          const cat = categoryLabels[catKey];
          if (!cat) return null;
          const CatIcon = cat.icon;
          return (
            <SectionCard
              key={catKey}
              id={`templates-${catKey}`}
              title={cat.title}
              icon={CatIcon}
              description={cat.desc}
            >
              <p className="text-[10px] text-[#999] mb-3 flex items-center gap-1">
                <AlertTriangle size={10} />
                Ogni SID inizia con HX... e lo trovi nella sezione Content Template di Twilio
              </p>
              {templateKeys.map(tKey => (
                <FieldRow key={tKey} label={templateLabels[tKey] || tKey}>
                  <input
                    type="text"
                    value={config.template_sids[tKey] || ''}
                    onChange={e => updateTemplate(tKey, e.target.value)}
                    placeholder={`HXxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx`}
                    data-testid={`input-template-${tKey}`}
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D44E20]/20 focus:border-[#D44E20] font-mono text-[#333] placeholder:text-[#ccc]"
                  />
                </FieldRow>
              ))}
            </SectionCard>
          );
        })}
      </div>

      {/* Save button */}
      <div className="sticky bottom-0 bg-gradient-to-t from-gray-50 via-gray-50 to-transparent pt-6 pb-4 mt-6">
        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-integrations-btn"
          className="w-full md:w-auto flex items-center justify-center gap-2 px-8 py-3 rounded-xl text-sm font-semibold bg-[#D44E20] text-white hover:bg-[#B8401A] disabled:opacity-50 shadow-lg shadow-[#D44E20]/20 transition-all"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Salva configurazione
        </button>
      </div>
    </div>
  );
}
